// ============================================================================
// components/remote/RemoteTransportSelector.tsx — выбор транспорта ХОСТА
// «Удалённого доступа»: LiveKit (WebRTC) | наш wire-контракт в локальной сети
// | ЧЕРЕЗ СЕРВЕР (релей за NAT). Вынесено из RemoteHostPanel (SRP).
// ============================================================================

import { Network, Radio, Server } from 'lucide-react';
import type { RemoteHostTransport } from '@/lib/remote/remoteHost';

interface TransportOption {
  code: RemoteHostTransport;
  label: string;
  hint: string;
}

const TRANSPORTS: TransportOption[] = [
  { code: 'livekit', label: 'LiveKit (WebRTC)', hint: 'Медиа кодирует SDK LiveKit; работает в корпоративных сетях.' },
  { code: 'contract', label: 'Наш контракт (WebSocket)', hint: 'Хост слушает порт в локальной сети; зритель подключается напрямую (LAN).' },
  { code: 'relay', label: 'Через сервер (релей)', hint: 'Хост и зритель соединяются исходящими через сервер — работает за NAT, входящие порты не нужны.' },
];

/** Подпись транспорта по коду (для строки состояния хоста). */
export function remoteTransportLabel(code: string): string {
  return TRANSPORTS.find((t) => t.code === code)?.label || '';
}

function transportIcon(code: RemoteHostTransport) {
  if (code === 'relay') return <Server className="h-3.5 w-3.5" />;
  if (code === 'contract') return <Network className="h-3.5 w-3.5" />;
  return <Radio className="h-3.5 w-3.5" />;
}

interface Props {
  value: RemoteHostTransport;
  disabled?: boolean;
  /** Доступные транспорты (нативно — LiveKit/контракт; в браузере — релей). */
  allowed?: RemoteHostTransport[];
  onChange: (t: RemoteHostTransport) => void;
}

export function RemoteTransportSelector({ value, disabled, allowed, onChange }: Props) {
  const options = allowed ? TRANSPORTS.filter((t) => allowed.includes(t.code)) : TRANSPORTS;
  return (
    <div className="flex flex-col gap-1 text-xs">
      Транспорт
      <div className="flex flex-wrap gap-2">
        {options.map((t) => (
          <button
            key={t.code}
            type="button"
            disabled={disabled}
            onClick={() => onChange(t.code)}
            title={t.hint}
            className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs disabled:opacity-60 ${
              value === t.code ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground'
            }`}
          >
            {transportIcon(t.code)}
            {t.label}
          </button>
        ))}
      </div>
      <span className="text-[10px] text-muted-foreground">{TRANSPORTS.find((t) => t.code === value)?.hint}</span>
    </div>
  );
}
